import type { FC, ReactNode } from 'react'

import { createContext, useCallback, useContext, useRef, useState } from 'react'
import { CircleHelp, TriangleAlert } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Dialog } from '@/components/ui/dialog'

type ConfirmOptions = {
    title: string
    description?: ReactNode
    confirmLabel?: string
    cancelLabel?: string
    tone?: 'default' | 'danger'
}

type ConfirmFn = (options: ConfirmOptions) => Promise<boolean>

const ConfirmContext = createContext<ConfirmFn | null>(null)

export const ConfirmProvider: FC<{ children: ReactNode }> = ({ children }): ReactNode => {
    const [options, setOptions] = useState<ConfirmOptions | null>(null)
    const [open, setOpen] = useState(false)
    const resolver = useRef<((value: boolean) => void) | null>(null)

    const confirm = useCallback<ConfirmFn>(next => {
        return new Promise<boolean>(resolve => {
            resolver.current = resolve
            setOptions(next)
            setOpen(true)
        })
    }, [])

    const settle = (value: boolean): void => {
        resolver.current?.(value)
        resolver.current = null
        setOpen(false)
    }

    const tone = options?.tone ?? 'danger'

    return (
        <ConfirmContext.Provider value={confirm}>
            {children}

            <Dialog
                open={open}
                onClose={() => settle(false)}
                size='sm'
                tone={tone}
                icon={tone === 'danger' ? <TriangleAlert /> : <CircleHelp />}
                title={options?.title ?? ''}
                description={options?.description}
                footer={(
                    <>
                        <Button variant='outline' onClick={() => settle(false)}>
                            {options?.cancelLabel ?? 'Batal'}
                        </Button>

                        <Button variant={tone === 'danger' ? 'danger' : 'primary'} onClick={() => settle(true)}>
                            {options?.confirmLabel ?? 'Ya, lanjutkan'}
                        </Button>
                    </>
                )}
            />
        </ConfirmContext.Provider>
    )
}

export const useConfirm = (): ConfirmFn => {
    const context = useContext(ConfirmContext)

    if (!context) throw new Error('useConfirm must be used inside ConfirmProvider')

    return context
}
